import React, { useEffect, useRef } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { getRoleFromToken } from "../utils/roleUtils";

/**
 * A unified ProtectedRoute component to handle role-based access control.
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - The component to render if authorized
 * @param {Array<string>} props.allowedRoles - List of roles that can access this route (e.g., ['ROLE_USER'])
 * @param {string} props.fallbackPath - Path to redirect if unauthorized (defaults based on role)
 */
const ProtectedRoute = ({ children, allowedRoles, fallbackPath }) => {
    const token = localStorage.getItem("user");
    const location = useLocation();
    const hasShownToast = useRef(false);

    const roleInfo = getRoleFromToken(token);
    const isAdmin = roleInfo?.isAdmin;
    const isAuthorized = roleInfo && allowedRoles.some(role =>
        (Array.isArray(roleInfo.decoded.role) && roleInfo.decoded.role.some(r =>
            r.authority === role || r === role || r === role.replace("ROLE_", "")
        )) ||
        roleInfo.decoded.role === role ||
        roleInfo.decoded.role === role.replace("ROLE_", "")
    );

    useEffect(() => {
        if (token && roleInfo && !isAuthorized && !hasShownToast.current) {
            hasShownToast.current = true;
            toast.error("Access denied. You don't have permission to access this page.", {
                position: toast.POSITION.TOP_RIGHT,
            });
        }
    }, [token, roleInfo, isAuthorized]);

    if (!token) {
        // Not logged in, redirect to login with the current path as state to return after login
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    if (!roleInfo) {
        // Invalid token, redirect to login
        localStorage.removeItem("user");
        return <Navigate to="/login" replace />;
    }

    if (!isAuthorized) {
        // Logged in but not authorized for this specific route
        // If it's an admin trying to access user routes, or vice versa
        const defaultFallback = isAdmin ? "/admin" : "/";
        return <Navigate to={fallbackPath || defaultFallback} replace />;
    }

    return children;
};

export default ProtectedRoute;
